import { string } from 'prop-types';
import { useLocation, useRouteError } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import {
  Heading,
  InlineTextButton,
  LayoutSingleColumn,
  Logo,
  NamedRedirect,
  PrimaryButton,
} from '../../components';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { pathByRouteName } from '../../util/routes';
import { clearChunkReloadAttempts, isChunkLoadError } from '../../util/chunkReload';

import css from './RouteErrorElement.module.css';

const isUnauthenticated = (error) => {
  return error?.status === 401;
};

const isForbidden = (error) => {
  return error?.status === 403;
};

const RouteErrorElement = ({ authPage = 'SignupPage' }) => {
  const error = useRouteError();
  const routeConfiguration = useRouteConfiguration();
  const location = useLocation();
  const { pathname, search, hash } = location;

  // eslint-disable-next-line no-console
  console.error(error);

  if (isUnauthenticated(error) || isForbidden(error)) {
    // When the loader function throws an error with status 401, we redirect to the auth page
    // with the current location as the state. This way the auth page can redirect back to the current
    return (
      <NamedRedirect
        name={authPage}
        state={
          location.state ?? {
            from: pathname + search + hash,
          }
        }
      />
    );
  }

  const handleOnClick = () => {
    const landingPagePath = pathByRouteName('LandingPage', routeConfiguration);
    if (typeof window !== 'undefined') {
      window.location.href = landingPagePath;
    }
  };

  const landingPageLink = (
    <InlineTextButton onClick={handleOnClick}>
      <FormattedMessage id="LoadableComponentErrorBoundaryPage.landingPageLink" />
    </InlineTextButton>
  );

  // We only get here on a chunk failure once util/chunkReload.js has spent its
  // reload budget, so the automatic recovery has already been tried and failed.
  // Saying "404" at that point is actively misleading — the page exists, this
  // device just can't assemble it.
  const isChunkError = isChunkLoadError(error?.message);

  const handleRetry = () => {
    if (typeof window === 'undefined') return;
    // The budget is spent, so reloading on its own would fail exactly as
    // before and land straight back here. Hand the automatic recovery a fresh
    // budget first: the reload fetches new HTML (which is never cached), and
    // by the time a user is pressing this button a deploy may well have
    // replaced the chunks it references.
    clearChunkReloadAttempts();
    window.location.reload();
  };

  const content = isChunkError ? (
    <div className={css.content}>
      <Heading as="h1" rootClassName={css.heading}>
        <FormattedMessage id="RouteErrorElement.chunkError.heading" />
      </Heading>
      <p className={css.description}>
        <FormattedMessage id="RouteErrorElement.chunkError.description" />
      </p>
      <div className={css.actions}>
        <PrimaryButton onClick={handleRetry}>
          <FormattedMessage id="RouteErrorElement.chunkError.retry" />
        </PrimaryButton>
      </div>
      <p className={css.description}>
        <FormattedMessage
          id="RouteErrorElement.chunkError.fallback"
          values={{ link: landingPageLink }}
        />
      </p>
    </div>
  ) : (
    <div className={css.content}>
      <div className={css.number}>404</div>
      <Heading as="h1" rootClassName={css.heading}>
        <FormattedMessage id="LoadableComponentErrorBoundaryPage.heading" />
      </Heading>
      <p className={css.description}>
        <FormattedMessage
          id="LoadableComponentErrorBoundaryPage.description"
          values={{ link: landingPageLink }}
        />
      </p>
    </div>
  );

  return (
    <div>
      <LayoutSingleColumn
        topbar={
          <div className={css.topbar}>
            <InlineTextButton onClick={handleOnClick}>
              <Logo className={css.logoMobile} layout="mobile" />
              <Logo className={css.logoDesktop} layout="desktop" />
            </InlineTextButton>
          </div>
        }
        footer={null}
      >
        <div className={css.root}>{content}</div>
      </LayoutSingleColumn>
    </div>
  );
};

RouteErrorElement.propTypes = {
  authPage: string,
};

export default RouteErrorElement;
